import { createContext } from '@direct-frontend/modules-context';
import type { Middleware } from '@reduxjs/toolkit';

import type { ModuleStore } from './types';

type AppliedMiddleware = ReturnType<ReturnType<Middleware>>;

type Context = Map<
    string,
    {
        store: ModuleStore;
        composedDynamicMiddleware?: AppliedMiddleware;
        lastComposedMiddlewareIndex?: number;
    }
>;

const { getContext } = createContext<Context>({
    name: '@direct-frontend/modules-store/storeManager',
    getInitialValue: () => new Map(),
});

/**
 * StoreManager, хранящий все сторы созданные модулями.
 *
 * ВНУТРЕННИЙ ЭКСПОРТ. ИСПОЛЬЗОВАТЬ В КОДЕ МОДУЛЕЙ ЗАПРЕЩЕНО.
 */
export const storeManager = {
    getStore(key: string) {
        return getContext().get(key)?.store;
    },
    getAllStores() {
        return Array.from(getContext().entries()).reduce<Record<string, ModuleStore>>((acc, [key, value]) => {
            const store = value.store;

            if (store) {
                acc[key] = store;
            }

            return acc;
        }, {});
    },
    hasStore(key: string) {
        try {
            return Boolean(getContext().get(key)?.store);
        } catch {
            return false;
        }
    },
    setStore(key: string, store: ModuleStore) {
        return getContext().set(key, {
            store,
        });
    },
    getComposedDynamicMiddleware(key: string) {
        return getContext().get(key)?.composedDynamicMiddleware;
    },
    setComposedDynamicMiddleware(key: string, composedDynamicMiddleware: AppliedMiddleware) {
        const value = getContext().get(key);

        if (value) {
            value.composedDynamicMiddleware = composedDynamicMiddleware;
        }
    },
    getLastComposedMiddlewareIndex(key: string) {
        return getContext().get(key)?.lastComposedMiddlewareIndex;
    },
    setLastComposedMiddlewareIndex(key: string, lastComposedMiddlewareIndex: number) {
        const value = getContext().get(key);

        if (value) {
            value.lastComposedMiddlewareIndex = lastComposedMiddlewareIndex;
        }
    },
    clear() {
        getContext().clear();
    },
};
